(ns dre.pages.frontpage.topic-search.events
  (:require [clojure.string]
            [clojure.walk :as walk]
            [day8.re-frame.tracing :refer-macros [fn-traced]]
            [dre.components.searchbar.util :as searchbar-util]
            [dre.components.searchbar.suggestion-util :refer [new-suggestions]]
            [dre.i18n.views :refer [tr]]
            [dre.pages.frontpage.topic-search.templates.diagnosis :as dg]
            [dre.pages.frontpage.topic-search.templates.efficacy-safety :as es]
            [dre.pages.frontpage.topic-search.templates.healthcare-economics :as he]
            [dre.pages.frontpage.topic-search.templates.prevalence :as pr]
            [dre.pages.frontpage.topic-search.templates.risk-prognosis :as rp]
            [dre.pages.frontpage.topic-search.templates.treatment-patterns :as tp]
            [dre.network :as-alias network]
            [dre.utils :refer [stringify-keys-with-namespaces aborted-request?]]
            [dre.shared.interface.search.pills :as pills]
            [dre.shared.interface.search.util :refer [normalized-blunt-new]]
            [re-frame.core :as rf]
            [superstructor.re-frame.fetch-fx :as-alias fetch-fx]))

(def db-path [:pages :data :frontpage :topic-search])

(def topics-map
  (into {} [pr/template es/template rp/template he/template tp/template dg/template]))

(defn get-pill-label
  [pill]
  (if (= (:pill/type pill) :text)
    (:pill/query pill)
    (:pill/label pill)))

(defn create-binary-terms
  "Creates AND or OR pills "
  [term vector]
  (if (= 1 (count vector))
    {:pill/type :and
     :pill/pills [(first vector)]}
    {:pill/type term
     :pill/pills vector}))

#_(defn common-elements
  "returns true if all the vectors provided contain at least one common element"
  [vectors]
  (let [bool-list (for
                   [v vectors
                    ele v]
                    (for
                     [_v vectors]
                      (boolean (some #{ele} _v))))]
    (some true? (map #(every? true? %1) bool-list))))

(defn- build-multiple
  "build OR or AND terms based on buisness rules"
  [type terms operator]
  (when (seq terms)
    (if (= 1 (count terms))
      (first terms)
      (create-binary-terms operator
                           (reverse terms)))))

(defn- build-query
  "Takes a templete of terms and populate the terms provided in it"
  [selected-topic template-string items operators]
  (let [default-items (into {} (map #(hash-map (:form-id %) []) (get-in topics-map [selected-topic :forms])))
        default-operators (into {} (map #(hash-map (:form-id %) (:operator %)) (get-in topics-map [selected-topic :forms])))
        built-items (->> (merge default-items items)
                         (map (fn [[k v]]
                                (hash-map k (let [v* (get-in v [:search-query :query/pills] [])
                                                  v* (cond
                                                       (and (empty? v*)
                                                            (= selected-topic :diagnosis)
                                                            (= k :outcome))
                                                       [{:pill/type :term
                                                         :pill/query "SUBsL8"
                                                         :pill/label "Accuracy"}]
                                                       :else v*)]
                                              v*))))

                         (into {})
                         (map (fn [[k v]]
                                (hash-map k (build-multiple k v (or (get operators k) (get default-operators k))))))
                         (reduce (fn [acc ele]
                                   (merge acc ele))))]
    ;; populating the templete
    (walk/postwalk
     (fn [node]
       (cond
         (and (map? node) (contains? node :template-type) (not (some #(= % (:template-type node)) (keys built-items))))
         nil
         (and (map? node) (contains? node :template-type) (some #(= % (:template-type node)) (keys built-items)))
         (let [node-keys (keys node)
               new-node (cond
                          (nil? (get built-items (:template-type node)))
                          nil
                          (= 1 (count node-keys))
                          (get built-items (:template-type node))
                          :else
                          (-> node
                              (assoc :pill/pill (get built-items (:template-type node)))
                              (dissoc :template-type)))]
           new-node)
         :else
         node))
     template-string)))

;; events

(rf/reg-event-db
 ::select-view
 [(rf/path db-path) rf/trim-v]
 (fn [db [view]]
   (assoc db :selected-view view)))

(rf/reg-event-fx
 ::expand-topic
 (fn [_ [_ topic]]
   {:fx [[:dispatch-n (list [::select-topic topic] [::select-view :forms])]]}))

(rf/reg-event-fx
 ::select-topic
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [topic-name]]
  (let [topic (get topics-map topic-name)
        forms (:forms topic)]
    {:db (assoc db :selected-topic topic-name)
     :fx (remove nil? (map (fn [form]
                             (if (seq (get-in db [:default-form-examples :data topic-name (:form-id form)] nil))
                               nil
                               [:dispatch [::complete-blueprints (:examples form) :default-form-examples topic-name (:form-id form)]])) forms))})))

(rf/reg-event-fx
 ::complete-blueprints
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [examples type topic-name form-id]]
  {:db (assoc-in db [type :state topic-name form-id] :loading)
   ::network/request [{:endpoint :dictionary/complete-blueprints}
                      {:body (stringify-keys-with-namespaces {:query/blueprints examples})
                       :on-success [::complete-blueprints-success {:type type :topic-name topic-name :form-id form-id}]}]}))

(rf/reg-event-fx
 ::complete-blueprints-success
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [type topic-name form-id ::fetch-fx/response]}]]
  (let [body (:body response)
        pills (pills/coerce (:query/pills body))]
    {:db (-> db
             (assoc-in [type :data topic-name form-id] pills)
             (assoc-in [type :state topic-name form-id] :success))})))

(rf/reg-event-fx
 ::fetch-terms-count
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} []]
  (let [terms (get (get db :ui) (:selected-topic db))
        topic (get topics-map (:selected-topic db))
        sq-template (:search-query-templates topic)
        selected-search-query-template-type (get-in db [:selected-search-query-template-type (:selected-topic db)]
                                                    (:default-search-query-template topic))
        query (build-query (:selected-topic db)
                           (get sq-template selected-search-query-template-type)
                           terms
                           (get (get db :operator) (:selected-topic db)))
        search-query-blunt (normalized-blunt-new {:query/pills [(:query/pill query)]})]
    {:db (-> db
             (assoc-in [:terms-count (:selected-topic db)] {})
             (assoc-in [:search-query-blunt (:selected-topic db)] search-query-blunt))
     :fx [[::network/request [{:endpoint :search/articles-old-count}
                              {:response-content-type :json
                               :body (stringify-keys-with-namespaces query)
                               :on-success [::terms-count-ok]
                               :on-failure [::terms-count-nok]}]]]})))

(rf/reg-event-db
 ::terms-count-ok
 [(rf/path db-path) rf/trim-v]
 (fn [db [{::fetch-fx/keys [response]}]]
   (assoc-in db [:terms-count (:selected-topic db)] (:body response))))

(rf/reg-event-db
 ::terms-count-nok
 [(rf/path db-path)]
 (fn-traced
  [{db :db} _]
  db))

(rf/reg-event-fx
 ::fetch-dynamic-examples
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [params]]
  (let [topic (get topics-map (:selected-topic db))
        forms (:forms topic)
        tt-types (set (flatten (remove nil? (map #(:top-terms-type %) forms))))
        terms (get (get db :ui) (:selected-topic db))
        sq-template (:search-query-templates topic)
        selected-search-query-template-type (get-in db [:selected-search-query-template-type (:selected-topic db)]
                                                    (:default-search-query-template topic))
        query (build-query (:selected-topic db)
                           (get sq-template selected-search-query-template-type)
                           terms
                           (get (get db :operator) (:selected-topic db)))
        search-query-blunt (normalized-blunt-new {:query/pills [(:query/pill query)]})
        body
        (-> {:query/blunt search-query-blunt}
            (assoc :categories tt-types
                   :k 20
                   :query/sentence-tag-filter "title"))]
    {:db (-> db
             (assoc-in [:form-examples-state (:selected-topic db)] :loading))
     :fx [[::network/request-with-abort [{:endpoint :topic-search/top-terms-fast}
                                         {:response-content-type :json
                                          :body (stringify-keys-with-namespaces body)
                                          :on-success [::fetch-dynamic-examples-ok (assoc params :selected-search-query-template-type selected-search-query-template-type)]
                                          :on-failure [::fetch-dynamic-examples-nok]}]]]})))

(rf/reg-event-fx
 ::fetch-dynamic-examples-ok
 [(rf/path db-path) rf/trim-v]
 (fn [{db :db} [{:keys [selected-search-query-template-type ::fetch-fx/response]}]]
   (let [extract-examples-fn (fn [top-terms-data top-terms-type selected-search-query-template-type top-terms-hardcoded-fn]
                               (if top-terms-type
                                 (let [terms (->> (map (fn [val]
                                                         {:pill/type :term
                                                          :pill/query (:sense val)
                                                          :pill/label (:label val)
                                                          :score (:count val)})
                                                       (apply concat (map #(get top-terms-data (keyword %)) top-terms-type)))
                                                  (sort-by :score)
                                                  reverse)]
                                   terms)
                                 (top-terms-hardcoded-fn selected-search-query-template-type)))]
     (let [topic (get topics-map (:selected-topic db))
           forms (:forms topic)
           top-terms-data (get-in response [:body :top-by])]
       {:db (assoc-in db [:form-examples-state (:selected-topic db)] :success)
        :dispatch-n (map (fn [form]
                           [::complete-blueprints (extract-examples-fn top-terms-data (:top-terms-type form)
                                                                       selected-search-query-template-type (:top-terms-hardcoded-fn form))
                            :form-examples (:selected-topic db) (:form-id form)]) forms)}))))

(rf/reg-event-fx
 ::fetch-dynamic-examples-nok
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [_ [{:keys [::fetch-fx/response] :as opts}]]
  (if (aborted-request? response)
    {}
    {:dispatch [:dre.network/default-failure opts]})))

(rf/reg-event-fx
 ::set-form-pills
 [(rf/path db-path) rf/trim-v]
 (fn [{db :db} [topic-name form value]]
   (let [search-query (get-in db [:ui topic-name form :search-query] {})
         current-pills (:query/pills search-query)
         new-pills (if (or (some #(= % value) current-pills)
                           (some #(and
                                   (= (:pill/query %) (:pill/query value))
                                   (= (:pill/type %) (:pill/type value))) current-pills))
                     (remove #(or (= value %)
                                  (and
                                   (= (:pill/query %) (:pill/query value))
                                   (= (:pill/type %) (:pill/type value))))
                             current-pills)
                     (conj current-pills value))
         new-db (assoc-in db [:ui topic-name form :search-query :query/pills] new-pills)]
     {:db new-db
      :dispatch-n [[::fetch-dynamic-examples]
                   [::fetch-terms-count]]})))

(rf/reg-event-fx
 ::set-selected-search-query-template-type
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [topic-name value]]
  {:db (assoc-in db [:selected-search-query-template-type topic-name] value)
   :dispatch-n [[::fetch-dynamic-examples]
                [::fetch-terms-count]]}))

(rf/reg-event-fx
 ::on-pills-change
 [(rf/path db-path) rf/trim-v]
 (fn [{db :db} [search-query]]
   (let []
     {:dispatch-n [[::fetch-dynamic-examples]
                   [::fetch-terms-count]]})))

(rf/reg-event-fx
 ::set-form-operator
 [(rf/path db-path) rf/trim-v]
 (fn [{db :db} [topic-name form value]]
   {:db (assoc-in db [:operator topic-name form] value)
    :dispatch-n [[::fetch-dynamic-examples]
                 [::fetch-terms-count]]}))

(rf/reg-event-fx
 ::fetch-suggestions
 [rf/trim-v]
 (fn-traced
  [{:keys [db]} [db-root-path {:keys [input limit] :as opts}]]
  {:pre [(vector? db-root-path)
         (searchbar-util/has-keys? opts #{:input :caret-position :search-query})]}
  (if-let [suggestions (get-in db (conj db-root-path :suggestions input))]
    {:db (update-in db db-root-path
                    #(assoc-in % [:suggestions :current] suggestions))}
    {::network/request
     [{:endpoint :dictionary/suggestions}
      {:params {:query input
                :limit (or limit 10)}
       :envelope? false
       :on-success [::fetch-suggestions-on-success db-root-path opts]}]})))

(rf/reg-event-db
 ::fetch-suggestions-on-success
 [rf/trim-v]
 (fn-traced
  [db [db-root-path {:keys [input] :as opts} {:keys [body]}]]
  {:pre [(vector? db-root-path)
         (searchbar-util/has-keys? opts #{:input :caret-position :search-query})]}
  (let [suggestions (assoc opts :data body)
        {:keys [input caret-position search-query]} suggestions
        suggestions (new-suggestions #_input input
                                     #_idx caret-position
                                     #_mode "subjects-w-umbrella-w-text"
                                     #_pills (-> search-query :query/pills (or []))
                                     #_dict-pills (searchbar-util/->suggestion-pills suggestions)
                                     #_signals nil
                                     #_tags nil)]
    (update-in db db-root-path
               #(-> %
                    (assoc-in [:suggestions input] suggestions)
                    (assoc-in [:suggestions :current] suggestions))))))
;; subscriptions

(rf/reg-sub
 ::state
 (fn [db _]
   (get-in db db-path)))

(rf/reg-sub
 ::selected-topic
 :<- [::state]
 (fn [state _]
   (:selected-topic state)))

(rf/reg-sub
 ::selected-view
 :<- [::state]
 (fn [state _]
   (:selected-view state)))

(rf/reg-sub
 ::get-form-pills
 :<- [::state]
 (fn [state [_ topic-name form]]
   (get-in state [:ui topic-name form :search-query :query/pills])))

(rf/reg-sub
 ::get-form-operator
 :<- [::state]
 (fn [state [_ topic-name form]]
   (get-in state [:operator topic-name form])))

(rf/reg-sub
 ::get-form-examples
 :<- [::state]
 (fn [state [_ topic-name form]]
   (if (seq (get-in state [:form-examples :data topic-name form]))
     (get-in state [:form-examples :data topic-name form])
     (get-in state [:default-form-examples :data topic-name form]))))

(rf/reg-sub
 ::is-form-examples-loading?
 :<- [::state]
 (fn [state [_ topic-name form]]
   (or (= :loading (get-in state [:form-examples-state topic-name]))
       (= :loading (get-in state [:default-form-examples :state topic-name form])))))

(rf/reg-sub
 ::is-form-filled?
 :<- [::state]
 (fn [state [_ topic-name forms]]
   (every? seq (for [f forms]
                 (get-in state [:ui topic-name f :search-query :query/pills])))))

(rf/reg-sub
 ::is-some-form-filled?
 :<- [::state]
 (fn [state [_ topic-name forms]]
   (or (not (seq forms))
        (some not-empty (for [f forms]
                          (get-in state [:ui topic-name f :search-query :query/pills]))))))

(rf/reg-sub
 ::get-terms-count
 :<- [::state]
 (fn [state [_ topic-name]]
   (get-in state [:terms-count topic-name])))

(rf/reg-sub
 ::get-search-query-blunt
 :<- [::state]
 (fn [state [_ topic-name]]
   (get-in state [:search-query-blunt topic-name])))

(rf/reg-sub
 ::get-selected-search-query-template-type
 :<- [::state]
 (fn [state [_ topic-name]]
   (get-in state [:selected-search-query-template-type topic-name])))
